import React, { useEffect, useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FormControl, FormControlLabel, Checkbox, makeStyles, TextField, Button } from "@material-ui/core"
import { useFormik, useFormikContext } from "formik"
import * as yup from "yup"
import "./index.css"
import { kbMakingTriggerRequest, kbMakingTriggerRequestClear } from "../../actions/bifrostActions"
import { useTranslation } from "react-i18next"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		margin: theme.spacing(2),
	},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
		alignItems: "center",
	},
	formActionsBtn: {
		margin: "0.5rem",
	},
	formDiv: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between",
		margin: theme.spacing(2),
	},
	formCheckBox: {
		display: "flex",
		flexDirection: "column",
		margin: theme.spacing(2),
	},
	textFieldClass: {
		width: "48%",
	},
	cancelBtn: {
		textTransform: "initial",
		fontSize: "16px",
		color: theme.palette.grey.D100,
		fontWeight: "400",
		"&:hover": {
			height: "40px",
		},
	},
	dividerLine: {
		width: "100%",
		border: `1px solid ${theme.palette.grey[400]}`,
	},
}))

const KbMaking = ({ data, callback, refreshCasePage }) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const history = useHistory()
	const [t] = useTranslation("global")

	//form validation
	const validationSchema = yup.object({
		case_id: yup.string(""),
		name: yup.string().trim().required(t("bifrost.kb_making.kbNameRequired")),
		lower_n: yup.number().min(1, t("bifrost.kb_making.lower_n_minText")),
		higher_n: yup
			.number()
			.moreThan(yup.ref("lower_n"), t("bifrost.kb_making.higher_n_validText"))
			.max(1000, t("bifrost.kb_making.higher_n_maxText")),
		max_ngram: yup.number().min(1, t("bifrost.kb_making.max_ngram_minText")),
		min_frequency: yup.number().min(1, t("bifrost.kb_making.min_freq_minText")),
	})

	const isSaved = useSelector((state) => state.bifrostCase.kbMaking)

	const [include_new_documents_only, setNewDocuments] = useState(true)
	const [include_duplicate_documents, setDuplicateDocuments] = useState(false)

	useEffect(() => {
		if (isSaved[data?.case_id]?.isKbMakingDone) {
			formik.resetForm()
			callback(false)
			history.push(`/bifrost/case/${data.case_id}`)
			dispatch(kbMakingTriggerRequestClear(data.case_id))
		}
	}, [isSaved])

	const onNewDocInputChange = (event) => {
		setNewDocuments(event.target.checked)
	}

	const onDuplicateDocsInputChange = (event) => {
		setDuplicateDocuments(event.target.checked)
	}

	const formik = useFormik({
		initialValues: {
			case_id: data.case_id,
			name: "",
			lower_n: 1,
			higher_n: 500,
			max_ngram: 3,
			min_frequency: 10,
		},
		validationSchema: validationSchema,
		onSubmit: (values) => {
			let KbConfig = {
				kb_name: values.name,
				include_duplicate_documents: include_duplicate_documents,
				include_new_documents_only: include_new_documents_only,
				count_config: {
					max_ngram: values?.max_ngram || 0,
					min_frequency: values.min_frequency || 0,
				},
			}
			if (values?.lower_n) KbConfig.lower_n = values?.lower_n
			if (values?.higher_n) KbConfig.higher_n = values?.higher_n
			const payload = {
				data: {
					case_id: values.case_id,
					kb_making_config: KbConfig,
				},
				refreshCasePage,
			}
			dispatch(kbMakingTriggerRequest(payload))
		},
	})

	return (
		<form className={classes.root} noValidate autoComplete="off" id="kb-trigger" onSubmit={formik.handleSubmit}>
			<FormControl fullWidth className={classes.margin}>
				<TextField
					id="name"
					label="KB Name*"
					variant="outlined"
					placeholder="Provide KB name"
					value={formik.values.name}
					onChange={formik.handleChange}
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name && formik.errors.name}
					autoFocus
				/>
			</FormControl>

			<FormControl fullWidth className={classes.formDiv}>
				<TextField
					className={classes.textFieldClass}
					id="lower_n"
					label="Lower_n"
					variant="outlined"
					value={formik.values.lower_n}
					inputProps={{ min: 1, max: 1000 }}
					type="number"
					onChange={formik.handleChange}
					error={formik.touched.lower_n && Boolean(formik.errors.lower_n)}
					helperText={formik.touched.lower_n && formik.errors.lower_n}
				/>
				<TextField
					className={classes.textFieldClass}
					id="higher_n"
					label="Higher_n"
					variant="outlined"
					value={formik.values.higher_n}
					type="number"
					inputProps={{ min: 1, max: 1000 }}
					onChange={formik.handleChange}
					error={formik.touched.higher_n && Boolean(formik.errors.higher_n)}
					helperText={formik.touched.higher_n && formik.errors.higher_n}
				/>
			</FormControl>
			<FormControl fullWidth className={classes.formCheckBox}>
				<FormControlLabel
					// className={classes.formDiv}
					control={
						<Checkbox checked={include_new_documents_only} onChange={onNewDocInputChange} name="new_docs" color="primary" />
					}
					label="Include new documents only"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={include_duplicate_documents}
							onChange={onDuplicateDocsInputChange}
							name="duplicate_docs"
							color="primary"
						/>
					}
					label="Include duplicate documents"
				/>
			</FormControl>

			<FormControl fullWidth className={classes.formDiv}>
				<TextField
					className={classes.textFieldClass}
					id="max_ngram"
					label="Max_ngram"
					variant="outlined"
					value={formik.values.max_ngram}
					type="number"
					inputProps={{ min: 1, max: 1000 }}
					onChange={formik.handleChange}
					error={formik.touched.max_ngram && Boolean(formik.errors.max_ngram)}
					helperText={formik.touched.max_ngram && formik.errors.max_ngram}
				/>
				<TextField
					className={classes.textFieldClass}
					id="min_frequency"
					label="Min_frequency"
					variant="outlined"
					value={formik.values.min_frequency}
					type="number"
					inputProps={{ min: 1, max: 1000 }}
					onChange={formik.handleChange}
					error={formik.touched.min_frequency && Boolean(formik.errors.min_frequency)}
					helperText={formik.touched.min_frequency && formik.errors.min_frequency}
				/>
			</FormControl>

			<hr className={classes.dividerLine} />

			<FormControl className={classes.formActions}>
				<Button variant="text" size="small" onClick={() => callback(false)} className={classes.cancelBtn}>
					{t("bifrost.buttonTexts.cancel")}
				</Button>
				<Button
					variant="contained"
					color="primary"
					classes={classes}
					className={classes.formActionsBtn}
					style={{
						textTransform: "initial",
						fontSize: "14px",
						padding: "8px 12px",
					}}
					type="submit"
				>
					{t("bifrost.buttonTexts.createKB")}
				</Button>
			</FormControl>
		</form>
	)
}

export default KbMaking
